import { useCallback, useEffect, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import "./cockpit/cockpit.css";
import TelemetryBar from "./cockpit/TelemetryBar";
import AttentionRail from "./cockpit/AttentionRail";
import LiveWorldMap from "./cockpit/LiveWorldMap";
import DecisionXRay from "./cockpit/DecisionXRay";
import TimeMachine from "./cockpit/TimeMachine";
import CommandBar from "./cockpit/CommandBar";
import ControlLab from "./cockpit/ControlLab";
import {
  cockpitLive, cockpitPerformance, cockpitLedger, cockpitDecision,
  cockpitAttention, cockpitMeta, cockpitTimeline, cockpitGps, cockpitDecisionChallenger,
} from "@/lib/rootConsole";

const iso = (ms) => new Date(ms).toISOString();
const H = 3600000;

// RootCockpit = orchestrateur de vue (poste de pilotage unique). Un seul état partagé, cross-link total.
export default function RootCockpit({ onAuthError, onLogout }) {
  const [live, setLive] = useState(null);
  const [perf, setPerf] = useState(null);
  const [att, setAtt] = useState(null);
  const [meta, setMeta] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [gps, setGps] = useState(null);
  const [err, setErr] = useState(null);

  const [selVehicle, setSelVehicle] = useState(null);
  const [selDecision, setSelDecision] = useState(null);
  const [challengerDiff, setChallengerDiff] = useState(null);
  const [mode, setMode] = useState("ops");
  const [selExp, setSelExp] = useState(null);

  const [windowHours, setWindowHours] = useState(24);
  const [endMs, setEndMs] = useState(() => Date.now());
  const fromMs = endMs - windowHours * H;
  const [tMs, setTMs] = useState(endMs);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const lastAutoRef = useRef(null);

  const handle = useCallback((e) => {
    const st = e?.response?.status;
    if ((st === 401 || st === 403) && onAuthError) { onAuthError(); return true; }
    setErr(e?.response?.data?.detail || String(e));
    return false;
  }, [onAuthError]);

  const loadLive = useCallback(async () => {
    try {
      const [l, p, a] = await Promise.all([cockpitLive(), cockpitPerformance(windowHours), cockpitAttention()]);
      setLive(l); setPerf(p); setAtt(a); setErr(null);
    } catch (e) { handle(e); }
  }, [windowHours, handle]);

  const loadTimeline = useCallback(async () => {
    try { setTimeline(await cockpitTimeline({ since: iso(endMs - windowHours * H), until: iso(endMs), limit: 1000 })); }
    catch (e) { handle(e); }
  }, [endMs, windowHours, handle]);

  useEffect(() => {
    loadLive();
    cockpitLedger(30, 0).then(setLedger).catch(handle);
    cockpitMeta().then(setMeta).catch(handle);
    const t = setInterval(loadLive, 10000);
    return () => clearInterval(t);
  }, [loadLive, handle]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  // GPS historique du véhicule sélectionné (fenêtré + downsamplé côté serveur)
  useEffect(() => {
    if (!selVehicle) { setGps(null); return; }
    let alive = true;
    cockpitGps(selVehicle, iso(fromMs), iso(endMs), 1500)
      .then((g) => alive && setGps(g)).catch(handle);
    return () => { alive = false; };
  }, [selVehicle, fromMs, endMs, handle]);

  const loadDetail = useCallback(async (id, { jump = false } = {}) => {
    if (!id) return;
    setSelDecision(id); setDetailLoading(true);
    try {
      const d = await cockpitDecision(id);
      setDetail(d);
      const dec = d?.decision || {};
      const rid = dec.assignment?.resource_id || dec.final_decision?.resource_id;
      if (rid) setSelVehicle(rid);
      if (jump && dec.at) { const ms = Date.parse(dec.at); if (ms) { setTMs(ms); lastAutoRef.current = id; } }
    } catch (e) { handle(e); } finally { setDetailLoading(false); }
  }, [handle]);

  const pickDecision = useCallback((id) => loadDetail(id, { jump: true }), [loadDetail]);

  // X-Ray Champion vs Challenger réel (SHADOW jumelle) — cross-link décision → diff
  useEffect(() => {
    if (!selDecision) { setChallengerDiff(null); return; }
    let alive = true;
    cockpitDecisionChallenger(selDecision).then((d) => alive && setChallengerDiff(d)).catch(() => {});
    return () => { alive = false; };
  }, [selDecision]);

  // Auto-X-Ray pendant le scrub/replay : décision active la plus proche de T (sans déplacer T)
  useEffect(() => {
    const evs = (timeline?.events || []).filter((e) => e.type === "decision" || e.type === "shadow");
    let nearest = null;
    for (const e of evs) { const ms = Date.parse(e.at); if (ms <= tMs && (!nearest || ms > Date.parse(nearest.at))) nearest = e; }
    if (nearest && nearest.decision_id && nearest.decision_id !== lastAutoRef.current && nearest.decision_id !== selDecision) {
      lastAutoRef.current = nearest.decision_id;
      loadDetail(nearest.decision_id);
    }
  }, [tMs, timeline, selDecision, loadDetail]);

  // Lecture (play) : avance T dans la fenêtre
  useEffect(() => {
    if (!playing) return;
    const span = endMs - fromMs;
    const id = setInterval(() => {
      setTMs((prev) => {
        const nx = prev + speed * (span / 200);
        if (nx >= endMs) { setPlaying(false); return endMs; }
        return nx;
      });
    }, 400);
    return () => clearInterval(id);
  }, [playing, speed, endMs, fromMs]);

  const changeWindow = (h) => { const now = Date.now(); setWindowHours(h); setEndMs(now); setTMs(now); setPlaying(false); };
  const seek = (ms) => { setTMs(ms); setPlaying(false); };

  const selRes = (live?.resources || []).find((r) => r.id === selVehicle);
  const cmdContext = {
    decisionId: selDecision,
    vehicleName: selRes?.name || selRes?.vehicle_id || (selVehicle || null),
    missionId: detail?.decision?.final_decision?.mission_id || detail?.decision?.assignment?.mission_id || selRes?.current_mission?.id,
    tMs,
    mode,
    experimentId: mode === "lab" ? selExp : null,
  };

  return (
    <div className="cockpit-root" data-testid="root-cockpit">
      <TelemetryBar perf={perf} meta={meta} live={live} windowHours={windowHours} onLogout={onLogout} mode={mode} onMode={setMode} />
      {err && <div className="ck-err" data-testid="cockpit-error">{err}</div>}

      <div className="ck-body">
        <PanelGroup direction="vertical" autoSaveId="ck-vert">
          <Panel defaultSize={70} minSize={40}>
            {mode === "lab" ? (
              <ControlLab selectedExpId={selExp} onSelectExp={setSelExp} />
            ) : (
              <PanelGroup direction="horizontal" autoSaveId="ck-horz">
                <Panel defaultSize={24} minSize={16}>
                  <AttentionRail attention={att} recentDecisions={ledger?.decisions} onPickDecision={pickDecision} />
                </Panel>
                <PanelResizeHandle className="ck-rh" />
                <Panel defaultSize={46} minSize={28}>
                  <LiveWorldMap resources={live?.resources} selectedVehicle={selVehicle}
                    onSelectVehicle={setSelVehicle} replay={selVehicle ? gps : null} tMs={tMs} />
                </Panel>
                <PanelResizeHandle className="ck-rh" />
                <Panel defaultSize={30} minSize={20}>
                  <DecisionXRay detail={detail} loading={detailLoading} onPickDecision={pickDecision} challengerDiff={challengerDiff} />
                </Panel>
              </PanelGroup>
            )}
          </Panel>
          <PanelResizeHandle className="ck-rh ck-rh-h" />
          <Panel defaultSize={30} minSize={16}>
            <TimeMachine events={timeline?.events} fromMs={fromMs} toMs={endMs} tMs={tMs}
              onSeek={seek} playing={playing} onTogglePlay={() => setPlaying((v) => !v)}
              speed={speed} onSpeed={setSpeed} windowHours={windowHours} onWindow={changeWindow}
              onPickDecision={pickDecision} />
          </Panel>
        </PanelGroup>
      </div>

      <CommandBar context={cmdContext} onAuthError={onAuthError} />
    </div>
  );
}
